import React from 'react';
import GenericSearchBox from '../../../../generic/GenericSearchBox';
import Loader from '../../../../Loader';
import { dataToValueOption, dataToTypeOption, SINGLE_ELEMENT_VIEW, ELEMENT_TYPES, INITIAL_LIMIT } from '../valuePickerUtils';
import { Alert, Modal } from 'react-bootstrap';
import ValuePickerFilter from '../ValuePickerFilter';
import { connect } from 'react-redux';
import { updateValuePickerFilters } from '../../../../../actions/reduxActions/valuePickerFilters';

const SYSTEM_VALUES = ['$State', '$User', '$JoinUri', '$True', '$False'];
const VALUE_TYPES_NOT_TO_EDIT = ['ContentObject', 'ContentList', 'ContentContent', 'ContentDateTime'];

// This is to determine whether to display an edit button for the value/type.
// We do not want edit system values.
export const isNonEditableValue = valueAsMeta => valueAsMeta.parentContentType ||
    VALUE_TYPES_NOT_TO_EDIT.some(type => type === valueAsMeta.contentType) ||
    SYSTEM_VALUES.some(systemValue => systemValue === valueAsMeta.developerName);

/**
 * @param {Array} errors An array of objects describing error messages received when retrieving values/types from the engine
 * @param {Function} clearError Callback for removing a specific error message
 * @param {Array} elements A list of values/types to be displayed
 * @param {Function} renderOption Callback that returns jsx snippet representing a single value/type
 * @param {Function} select Callback to trigger a value/type selection
 * @param {Function} switchView Callback for switching between the list view and create/edit view
 * @param {Boolean} isLoading whether to display a loading indicator
 * @param {String} elementType What type of element this selector is selecting e.g. 'value' or 'type' default: 'value'
 * @param {Function} updateFilters Update the filters in the store
 * @param {Function} close Close the modal
 * @param {Array} recentValues A list of recently used Values
 * @description A component that lists values/types and allows for searching for specific values/types
 */
export const ElementList = ({
    errors,
    clearError,
    elements,
    renderOption,
    select,
    switchView,
    isLoading,
    elementType,
    updateFilters,
    close,
    recentValues }) => {

    const search = manywho.utils.debounce(updateFilters, 750);

    const onSearchChange = (event) => {
        search('search', event.target.value);
    };

    const renderList = (elementList, className = 'element-list') => (
        <ul className={`picker-options ${className}`}>
            {
                elementList.map((element) => {

                    const elementAsMeta = elementType === ELEMENT_TYPES.value ?
                        dataToValueOption(element) :
                        dataToTypeOption(element);

                    const key = `${element.id}-${element.typeElementPropertyId}`;

                    return (
                        <li className="value-list-item" key={key}>
                            <button onClick={() => select(element)} className="picker-option">
                                {
                                    renderOption(elementAsMeta)
                                }
                            </button>
                            {
                                isNonEditableValue(elementAsMeta) ?
                                    null :
                                    (
                                        <button
                                            className="value-button"
                                            onClick={() => switchView(SINGLE_ELEMENT_VIEW, elementAsMeta)}
                                        >
                                            Edit
                                        </button>
                                    )
                            }
                        </li>
                    );
                })
            }
        </ul>
    );

    return (
        <>
            <Modal.Header>
                <div className="picker-search-wrapper">
                    <div className="picker-search-inner">
                        <GenericSearchBox
                            onSearchChange={onSearchChange}
                            hasSearchButton={false}
                            hasAutoFocus
                        />
                    </div>
                    <span className="search-splitter">or</span>
                    <button
                        onClick={() => switchView(SINGLE_ELEMENT_VIEW)}
                        className="btn btn-primary new-value"
                    >
                        {`Create a new ${elementType[0].toUpperCase()}${elementType.substring(1)}`}
                    </button>
                </div>
                <ValuePickerFilter />
            </Modal.Header>
            <Modal.Body className="picker-modal-body flex-column">
                {
                    errors.length > 0 ?
                        errors.map((error, index) => (
                            <Alert
                                onDismiss={() => clearError(index)}
                                bsStyle="danger"
                                key={index}
                            >
                                <p><strong>{error.message}</strong></p>
                            </Alert>
                        )) :
                        null
                }
                <>
                    {isLoading ? <Loader /> :
                        !isLoading && elements.length > 0 ?
                            (
                                <div className="overflow-y-auto">
                                    {
                                        elementType === ELEMENT_TYPES.value && recentValues.length > 0 ?
                                            (
                                                <>
                                                    <label className="margin-bottom-small">Recent</label>
                                                    {
                                                        renderList(recentValues, 'recent-value-list')
                                                    }
                                                    <hr className="picker-recent-separator" />
                                                </>
                                            ) : null
                                    }
                                    {
                                        // If not all items fetched, show limit-1 elements and a button to show the full list
                                        // Note - the /type endpoint returns one more row than the requested limit
                                        (elementType === ELEMENT_TYPES.value && elements.length === INITIAL_LIMIT) ||
                                        (elementType === ELEMENT_TYPES.type && elements.length === INITIAL_LIMIT + 1) ?
                                            (
                                                <>
                                                    { renderList(elements.slice(0, -1)) }
                                                    <button
                                                        onClick={() => updateFilters('limit', 0)}
                                                        className="value-picker-more-results"
                                                    >
                                                        More Results
                                                    </button>
                                                </>
                                            ) : renderList(elements)
                                    }
                                </div>
                            ) : <div className="mw-items-empty"><p className="lead">No Results</p></div>}
                </>
            </Modal.Body>
            <Modal.Footer>
                <button
                    onClick={close}
                    className="btn btn-default"
                >
                    Cancel
                </button>
            </Modal.Footer>
        </>
    );
};

const mapStateToProps = ownProps => ({
    ...ownProps,
});

const mapDispatchToProps = {
    updateFilters: updateValuePickerFilters,
};

export default connect(mapStateToProps, mapDispatchToProps)(ElementList);
